import { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Truck, MapPin, User } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '@/lib/AuthContext';
import { useRealtimeLoadPings } from '@/hooks/use-realtime-loads';
import { cn } from '@/lib/utils';
import { liveIcon, fetchPings, useTicker, formatAgo } from '@/components/LoadMapModal';

// Like LoadMapModal's shared FitBounds, but also closes any open marker
// popup first -- an open popup on the previously-selected truck was
// pinning the view, so re-fitting to a newly-selected truck moved the
// map's internal center but the popup (and the viewport around it)
// never visibly budged. Scoped to this file since LoadMapModal's other
// callers don't have this multi-marker-with-persistent-popups situation.
function FitFleetView({ coords, singleZoom = 8 }) {
  const map = useMap();
  useEffect(() => {
    map.closePopup();
    if (coords.length >= 2) {
      map.fitBounds(coords, { padding: [60, 60], animate: false });
    } else if (coords.length === 1) {
      map.setView(coords[0], singleZoom, { animate: false });
    }
  }, [coords, map, singleZoom]);
  return null;
}

// Roster row -- one truck (company- or owner-operator-owned) and the
// driver currently assigned to it, whichever of the two the "Trucks" /
// "Drivers" toggle has made the primary (bold) line right now -- plus
// whether that driver has a live GPS ping right now (only possible while
// their load is In Transit -- see ActiveLoad.jsx's location-sharing gate).
function RosterRow({ primaryText, secondaryText, isLive, isSelected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors",
        isSelected ? "bg-primary/15 border border-primary/30" : "border border-transparent hover:bg-white/5"
      )}
    >
      <span className={cn("w-2 h-2 rounded-full flex-shrink-0", isLive ? "bg-violet-400 animate-pulse" : "bg-muted-foreground/40")} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-foreground truncate">{primaryText}</p>
        <p className="text-[11px] text-muted-foreground truncate">{secondaryText}</p>
      </div>
      {!isLive && <span className="text-[10px] text-muted-foreground flex-shrink-0">Offline</span>}
    </button>
  );
}

// Dispatch > Map tab: a roster of every company + owner-operator truck on
// the left, the fleet map on the right. Selecting a truck focuses the map
// on that driver's live position; clearing the selection (or the initial
// state) zooms out to fit every driver currently reporting a location.
export default function FleetMapPanel({ loads, assets, driverRoster }) {
  const { carrierId } = useAuth();
  const [pingsByDriver, setPingsByDriver] = useState(new Map());
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [viewMode, setViewMode] = useState('truck'); // 'truck' | 'driver' -- which field is the bold/primary line
  const now = useTicker();

  const activeLoads = useMemo(
    () => loads.filter(l => l.status === 'In Transit' && l.assigned_driver_email),
    [loads]
  );
  const activeLoadsKey = activeLoads.map(l => l.id).join(',');

  const refetchPings = useCallback(() => {
    if (activeLoads.length === 0) { setPingsByDriver(new Map()); return; }
    Promise.all(activeLoads.map(l => fetchPings(l.id, 1))).then(results => {
      const map = new Map();
      activeLoads.forEach((l, i) => { if (results[i][0]) map.set(l.assigned_driver_email, results[i][0]); });
      setPingsByDriver(map);
    }).catch(() => {});
  }, [activeLoadsKey]);

  useEffect(() => { refetchPings(); }, [refetchPings]);
  useRealtimeLoadPings(carrierId, refetchPings);

  const driverNameByEmail = useMemo(() => {
    const m = new Map();
    driverRoster.forEach(d => m.set(d.user_email, d.full_name));
    return m;
  }, [driverRoster]);

  const trucks = useMemo(
    () => assets.filter(a => a.asset_type === 'Truck' && a.is_active !== false),
    [assets]
  );
  const sortKey = (t) => (viewMode === 'driver'
    ? driverNameByEmail.get(t.assigned_driver_email) || 'zzz'
    : t.unit_number || 'zzz');
  const companyTrucks = trucks.filter(t => t.ownership_type !== 'Non-Owned')
    .slice().sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  const ownerOpTrucks = trucks.filter(t => t.ownership_type === 'Non-Owned')
    .slice().sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

  // Clear a selection that's no longer trackable (driver went offline,
  // truck reassigned, etc.) rather than leaving the map stuck on a stale pin.
  useEffect(() => {
    if (selectedEmail && !pingsByDriver.has(selectedEmail)) setSelectedEmail(null);
  }, [selectedEmail, pingsByDriver]);

  // Memoized on `trucks`/`pingsByDriver` (not recomputed on the once-a-second
  // ticker re-render from useTicker) so FitBounds only re-fits when the
  // location data actually changes -- otherwise a fresh array identity
  // every second was fighting any manual zoom/pan the user just did.
  const positionedTrucks = useMemo(
    () => trucks.filter(t => pingsByDriver.has(t.assigned_driver_email)),
    [trucks, pingsByDriver]
  );
  const allCoords = useMemo(
    () => positionedTrucks.map(t => { const p = pingsByDriver.get(t.assigned_driver_email); return [p.lat, p.lng]; }),
    [positionedTrucks, pingsByDriver]
  );
  const selectedPing = selectedEmail ? pingsByDriver.get(selectedEmail) : null;
  const fitCoords = useMemo(
    () => (selectedPing ? [[selectedPing.lat, selectedPing.lng]] : allCoords),
    [selectedPing, allCoords]
  );

  const renderGroup = (label, list) => list.length > 0 && (
    <div>
      <p className="px-3 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      <div className="space-y-0.5 px-1.5">
        {list.map(t => {
          const unitText = `Unit #${t.unit_number || '—'}`;
          const driverText = driverNameByEmail.get(t.assigned_driver_email) || 'Unassigned';
          return (
            <RosterRow
              key={t.id}
              primaryText={viewMode === 'driver' ? driverText : unitText}
              secondaryText={viewMode === 'driver' ? unitText : driverText}
              isLive={pingsByDriver.has(t.assigned_driver_email)}
              isSelected={!!t.assigned_driver_email && selectedEmail === t.assigned_driver_email}
              onClick={() => setSelectedEmail(t.assigned_driver_email === selectedEmail ? null : t.assigned_driver_email)}
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="flex h-full">
      <div className="w-60 flex-shrink-0 border-r border-border overflow-y-auto">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <Truck className="w-3.5 h-3.5" /> Fleet
          </div>
          {selectedEmail && (
            <button
              type="button"
              onClick={() => setSelectedEmail(null)}
              className="text-[11px] text-primary hover:underline"
            >
              View all
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 p-1.5 border-b border-border">
          <button
            type="button"
            onClick={() => setViewMode('truck')}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors",
              viewMode === 'truck' ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Truck className="w-3 h-3" /> Trucks
          </button>
          <button
            type="button"
            onClick={() => setViewMode('driver')}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors",
              viewMode === 'driver' ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <User className="w-3 h-3" /> Drivers
          </button>
        </div>
        {trucks.length === 0 ? (
          <p className="px-3 py-4 text-xs text-muted-foreground">No trucks added yet.</p>
        ) : (
          <>
            {renderGroup(viewMode === 'driver' ? 'Company Drivers' : 'Company Trucks', companyTrucks)}
            {renderGroup(viewMode === 'driver' ? 'Owner Operator Drivers' : 'Owner Operator Trucks', ownerOpTrucks)}
          </>
        )}
      </div>
      <div className="flex-1 relative">
        <div className="absolute top-2 left-2 z-[1000] flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/80 border border-border text-[11px] text-muted-foreground">
          <div className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" /> Live position
        </div>
        <MapContainer center={[39.5, -98.35]} zoom={4} style={{ height: '100%', width: '100%' }} zoomAnimation={false}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          {fitCoords.length > 0 && <FitFleetView coords={fitCoords} singleZoom={10} />}
          {positionedTrucks.map(t => {
            const p = pingsByDriver.get(t.assigned_driver_email);
            return (
              <Marker
                key={t.id}
                position={[p.lat, p.lng]}
                icon={liveIcon}
                eventHandlers={{ click: () => setSelectedEmail(t.assigned_driver_email) }}
              >
                <Popup>
                  <strong>Unit #{t.unit_number} · Live</strong><br />
                  {driverNameByEmail.get(t.assigned_driver_email) || t.assigned_driver_email}<br />
                  Updated {formatAgo(p.recorded_at, now)}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
        {positionedTrucks.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/80 border border-border text-xs text-muted-foreground">
              <MapPin className="w-3.5 h-3.5" /> No drivers currently sharing a live location
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
